import { type FC } from "react";

import { Check } from "lucide-react";

import { Button } from "@/shared/components/ui/button";
import { TIER_CONFIGS } from "@/shared/constants/planTiers";

import type { PaymentTier } from "@/shared/utils/tierOrder";

const UPSELL_FEATURES = [
  "Estate's tax ID (EIN) and bank account opened for you, so you can cash checks and get access to funds",
  "Banks reached out to for you, so balances and beneficiaries are confirmed",
  "Every payment in and out tracked for you, with your paybacks and receipts in one place",
  "1:1 sessions with your Estate Expert to answer any questions",
];

function getUpgradeDisplay(currentTier: PaymentTier | null): {
  price: string;
  originalPrice: string | null;
  suffix: string | null;
} {
  const config = TIER_CONFIGS.platinum;

  if (currentTier === "premium") {
    return {
      price: config.upgradeFromPremiumPrice ?? config.price,
      originalPrice: config.price,
      suffix: "to upgrade from Asset Search",
    };
  }
  if (currentTier === "basic") {
    return {
      price: config.upgradeFromBasicPrice,
      originalPrice: config.price,
      suffix: "to upgrade from Estate Setup",
    };
  }
  return { price: config.price, originalPrice: null, suffix: null };
}

interface SettleUpsellCardProps {
  currentTier: PaymentTier | null;
  onUpgrade: () => void;
  isLoading: boolean;
}

export const SettleUpsellCard: FC<SettleUpsellCardProps> = ({
  currentTier,
  onUpgrade,
  isLoading,
}) => {
  const { price, originalPrice, suffix } = getUpgradeDisplay(currentTier);

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-brand bg-brand-subtle p-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:gap-3">
        <div className="flex flex-1 flex-col gap-2">
          <h4>Cover the bills, get reimbursed, pass the rest on</h4>
          <p className="text-xs text-secondary">
            We apply for the estate's tax ID and help you open its bank account, so bills, your
            reimbursements, and what's left move through the estate
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-start gap-1 sm:items-end">
          <div className="flex items-start gap-2">
            {originalPrice ? (
              <span className="text-xl font-semibold text-tertiary line-through">
                {originalPrice}
              </span>
            ) : null}
            <span className="text-xl font-semibold">{price}</span>
          </div>
          {suffix ? <p className="text-xs text-secondary">{suffix}</p> : null}
        </div>
      </div>

      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-2">
          {UPSELL_FEATURES.map((feature) => (
            <div key={feature} className="flex items-start gap-2">
              <Check className="h-4 w-4 shrink-0 text-brand mt-0.5" />
              <p className="text-xs">{feature}</p>
            </div>
          ))}
          <p className="text-xs text-secondary">
            An eligible estate expense, paid back by the estate
          </p>
        </div>
        <Button variant="brand" className="w-full sm:w-auto sm:self-end" onClick={onUpgrade} disabled={isLoading}>
          {isLoading ? "Loading..." : "Get Estate Settlement"}
        </Button>
      </div>
    </div>
  );
};
